import { Button, Modal, Tag, Tooltip } from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import {
    formatCurrency,
    getLocationLabel,
    getProviderKey,
    isSalePaid,
    PROVIDER_LABELS,
} from "../../lib/insuranceSaleApi";
import useInsuranceSaleSendPaymentLink from "../../hooks/useInsuranceSaleSendPaymentLink";
import {
    normalizePaymentEvents,
    PAYMENT_EVENT_STATUS_CONFIG,
} from "../../../reservations/lib/reservationInvoiceHelpers";

const formatDateTime = (value) => (value ? moment(value).format("MMM D, YYYY h:mm A") : "—");

const formatPhone = (value) => {
    if (!value) return "—";
    const parsed = parsePhoneNumberFromString(`+${String(value).replace(/^\+/, "")}`);
    return parsed?.formatNational?.() || value;
};

const processLabel = (process, t) => {
    if (process === "insuranceSaleExtension") return t("invoice.extensionPayment");
    if (process === "insuranceSale") return t("invoice.originalPayment");
    return process || t("invoice.payment");
};

const statusLabelKey = (status) => {
    if (status === "success") return "invoice.paymentSuccess";
    if (status === "failed") return "invoice.paymentFailed";
    return "invoice.paymentPending";
};

const PaymentStatusCell = ({ status, errorMessage, t }) => {
    const config = PAYMENT_EVENT_STATUS_CONFIG[status] ?? PAYMENT_EVENT_STATUS_CONFIG.pending;
    const label = t(statusLabelKey(status));
    const tag = (
        <Tag color={config.color} style={{ margin: 0 }}>
            <span style={{ color: config.printColor }}>{label}</span>
        </Tag>
    );

    if (status === "failed" && errorMessage) {
        return <Tooltip title={errorMessage}>{tag}</Tooltip>;
    }

    return tag;
};

PaymentStatusCell.propTypes = {
    status: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    t: PropTypes.func.isRequired,
};

PaymentStatusCell.defaultProps = {
    errorMessage: "",
};

const InsuranceSaleInvoiceModal = ({ open, sale, onClose, onRefresh }) => {
    const { t } = useTranslation("sellInsurance");
    const { myCompanyData } = useUser();
    const { sendPaymentLink, loading: sendLinkLoading, bookingAgentConfigured } =
        useInsuranceSaleSendPaymentLink();
    const printRef = useRef(null);
    const [printLoader, setPrintLoader] = useState(false);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: sale?.id ? `insurance-invoice-${sale.id}` : "insurance-invoice",
        pageStyle: `@page { size: A4; margin: 16mm; }`,
    });

    const onPrint = () => {
        setPrintLoader(true);
        handlePrint();
        setTimeout(() => setPrintLoader(false), 1500);
    };

    const invoice = sale?.invoice || {};
    const charges = Array.isArray(invoice.charges) ? invoice.charges : [];
    const taxes = Array.isArray(invoice.taxes) ? invoice.taxes : [];
    const payments = normalizePaymentEvents(invoice);
    const location = sale?.pickupLocation;
    const provider = PROVIDER_LABELS[getProviderKey(sale)] ?? "—";
    const amountDue = Number(invoice.due ?? 0);
    const showSendPaymentLink = sale?.id && !isSalePaid(sale) && amountDue > 0;

    const handleSendPaymentLink = () =>
        sendPaymentLink({
            reservationId: sale.id,
            amount: amountDue,
            customerEmail: sale.email,
            reservationProcess: "insuranceSale",
            onSuccess: onRefresh,
        });

    return (
        <Modal
            open={open}
            title={t("invoice.title")}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t("wizard.cancel")}
                </Button>,
                showSendPaymentLink ? (
                    <Button
                        key="sendLink"
                        loading={sendLinkLoading}
                        disabled={!bookingAgentConfigured}
                        title={bookingAgentConfigured ? undefined : t("pay.bookingAgentRequired")}
                        onClick={handleSendPaymentLink}>
                        {sendLinkLoading ? t("pay.sending") : t("pay.sendPaymentLink")}
                    </Button>
                ) : null,
                <Button key="print" type="primary" loading={printLoader} onClick={onPrint}>
                    {t("invoice.print")}
                </Button>,
            ].filter(Boolean)}
            width={{
                xs: "calc(100vw - 2rem)",
                sm: 760,
            }}
            centered
            destroyOnClose>
            <div ref={printRef} style={{ fontSize: "11pt", color: "#111" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 24, marginBottom: 24 }}>
                    <div>
                        {myCompanyData?.logo?.light || myCompanyData?.logo?.dark ? (
                            <img
                                src={myCompanyData.logo.light || myCompanyData.logo.dark}
                                alt=""
                                style={{ height: 48, marginBottom: 8 }}
                            />
                        ) : null}
                        <div style={{ fontWeight: 700 }}>{myCompanyData?.name}</div>
                        <div>{myCompanyData?.address?.address}</div>
                        <div>
                            {[myCompanyData?.address?.city, myCompanyData?.address?.state]
                                .filter(Boolean)
                                .join(", ")}
                        </div>
                        <div>{formatPhone(myCompanyData?.phone)}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{t("invoice.title")}</div>
                        <div>
                            {t("invoice.saleId")}: <span style={{ fontFamily: "monospace" }}>{sale?.id}</span>
                        </div>
                        <div>
                            {t("invoice.issued")}: {formatDateTime(sale?.createdAt || new Date())}
                        </div>
                    </div>
                </div>

                <table style={{ width: "100%", marginBottom: 20, borderCollapse: "collapse" }}>
                    <tbody>
                        <tr>
                            <td style={{ width: "50%", verticalAlign: "top", paddingRight: 16 }}>
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("invoice.billedTo")}</div>
                                <div>{sale?.customerName || "—"}</div>
                                <div>{sale?.email || "—"}</div>
                                <div>{formatPhone(sale?.mobileNumber)}</div>
                            </td>
                            <td style={{ width: "50%", verticalAlign: "top" }}>
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("invoice.coverage")}</div>
                                <div>
                                    {t("wizard.provider")}: {provider}
                                </div>
                                <div>
                                    {t("wizard.vehicle")}: {sale?.carName || "—"}
                                </div>
                                <div>
                                    {t("wizard.vin")}: {sale?.carSelected?.VIN || "—"}
                                </div>
                                <div>
                                    {t("wizard.pickupLocation")}:{" "}
                                    {location ? getLocationLabel(location) : "—"}
                                </div>
                                <div>
                                    {t("wizard.coverageStart")}: {formatDateTime(sale?.pickupDate)}
                                </div>
                                <div>
                                    {t("wizard.coverageEnd")}:{" "}
                                    {formatDateTime(sale?.exactDropoffTime || sale?.dropoffDate)}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
                    <thead>
                        <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                            <th style={{ padding: "8px 6px", borderBottom: "1px solid #d1d5db" }}>
                                {t("invoice.description")}
                            </th>
                            <th style={{ padding: "8px 6px", borderBottom: "1px solid #d1d5db" }}>
                                {t("invoice.days")}
                            </th>
                            <th style={{ padding: "8px 6px", borderBottom: "1px solid #d1d5db", textAlign: "right" }}>
                                {t("invoice.rate")}
                            </th>
                            <th style={{ padding: "8px 6px", borderBottom: "1px solid #d1d5db", textAlign: "right" }}>
                                {t("invoice.amount")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {charges.map((charge, index) => (
                            <tr key={`${charge.description}-${index}`}>
                                <td style={{ padding: "8px 6px", borderBottom: "1px solid #e5e7eb" }}>
                                    <div>{charge.description || t("invoice.insurance")}</div>
                                    {charge.explanation ? (
                                        <div style={{ fontSize: "9pt", color: "#6b7280" }}>
                                            {charge.explanation}
                                        </div>
                                    ) : null}
                                </td>
                                <td style={{ padding: "8px 6px", borderBottom: "1px solid #e5e7eb" }}>
                                    {charge.unit ?? "—"}
                                </td>
                                <td
                                    style={{
                                        padding: "8px 6px",
                                        borderBottom: "1px solid #e5e7eb",
                                        textAlign: "right",
                                    }}>
                                    {formatCurrency(charge.rate)}
                                </td>
                                <td
                                    style={{
                                        padding: "8px 6px",
                                        borderBottom: "1px solid #e5e7eb",
                                        textAlign: "right",
                                    }}>
                                    {formatCurrency(charge.total)}
                                </td>
                            </tr>
                        ))}
                        {taxes.map((tax, index) => (
                            <tr key={`${tax.taxName}-${index}`}>
                                <td style={{ padding: "8px 6px", borderBottom: "1px solid #e5e7eb" }} colSpan={3}>
                                    {tax.taxName}
                                    {tax.taxType === "%" ? ` (${tax.taxValue}%)` : ""}
                                </td>
                                <td
                                    style={{
                                        padding: "8px 6px",
                                        borderBottom: "1px solid #e5e7eb",
                                        textAlign: "right",
                                    }}>
                                    {formatCurrency(tax.total)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <table style={{ minWidth: 260, borderCollapse: "collapse" }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: "4px 8px" }}>{t("wizard.subtotal")}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right" }}>
                                    {formatCurrency(invoice.estimatedTotal)}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "4px 8px" }}>{t("wizard.taxes")}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right" }}>
                                    {formatCurrency(
                                        taxes.reduce((sum, tax) => sum + Number(tax.total || 0), 0),
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "4px 8px", fontWeight: 700 }}>{t("wizard.total")}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 700 }}>
                                    {formatCurrency(invoice.total)}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "4px 8px" }}>{t("invoice.paid")}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right" }}>
                                    {formatCurrency(invoice.deposit)}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "4px 8px" }}>{t("invoice.due")}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right" }}>
                                    {formatCurrency(invoice.due)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {payments.length > 0 ? (
                    <div style={{ marginTop: 24 }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("invoice.payments")}</div>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                                    <th style={{ padding: "6px", borderBottom: "1px solid #d1d5db" }}>
                                        {t("invoice.paymentType")}
                                    </th>
                                    <th style={{ padding: "6px", borderBottom: "1px solid #d1d5db" }}>
                                        {t("invoice.status")}
                                    </th>
                                    <th style={{ padding: "6px", borderBottom: "1px solid #d1d5db" }}>
                                        {t("invoice.platform")}
                                    </th>
                                    <th
                                        style={{
                                            padding: "6px",
                                            borderBottom: "1px solid #d1d5db",
                                            textAlign: "right",
                                        }}>
                                        {t("invoice.amount")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((event) => (
                                    <tr key={event.id}>
                                        <td style={{ padding: "6px", borderBottom: "1px solid #e5e7eb" }}>
                                            {processLabel(event.reservationProcess, t)}
                                        </td>
                                        <td style={{ padding: "6px", borderBottom: "1px solid #e5e7eb" }}>
                                            <PaymentStatusCell
                                                status={event.status}
                                                errorMessage={event.description}
                                                t={t}
                                            />
                                        </td>
                                        <td style={{ padding: "6px", borderBottom: "1px solid #e5e7eb" }}>
                                            {event.paymentPlatform || "—"}
                                        </td>
                                        <td
                                            style={{
                                                padding: "6px",
                                                borderBottom: "1px solid #e5e7eb",
                                                textAlign: "right",
                                            }}>
                                            {formatCurrency(event.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

InsuranceSaleInvoiceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    sale: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
};

InsuranceSaleInvoiceModal.defaultProps = {
    sale: null,
    onRefresh: null,
};

export default InsuranceSaleInvoiceModal;
